/**
 * FixedInfoPanel — fixed left panel showing the current project's info.
 * Driven by activeSectionIndex from sectionScrollStore.
 *
 * Text animates using overflow-hidden masks + GSAP SplitText:
 *   Exit  → split lines slide down to yPercent:105
 *   Entry → split lines reveal up from yPercent:105 → 0 with stagger
 *
 * Only active for project sections. Home and contact sections manage
 * their own content independently.
 */
import { ClientLogo } from "@/components/ui/ClientLogo"
import { useProjects } from "@/hooks/useProjects"
import { useSectionScrollStore } from "@/stores/sectionScrollStore"
import type { Project } from "@/types/portfolio"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { SplitText } from "gsap/SplitText"
import { useEffect, useRef, useState } from "react"

gsap.registerPlugin(SplitText)

export function FixedInfoPanel() {
  const activeSectionIndex = useSectionScrollStore((s) => s.activeSectionIndex)
  const sections = useSectionScrollStore((s) => s.sections)
  const { data: projects } = useProjects()

  const [displayedIndex, setDisplayedIndex] = useState(0)
  const displayedIndexRef = useRef(0)
  const contentRef = useRef<HTMLDivElement>(null)
  // Track the running exit timeline so we can kill it directly.
  // Killing the timeline (not its child tweens) prevents onComplete from
  // firing with a stale activeSectionIndex closure when section changes arrive
  // mid-animation (e.g. rapid scroll or ScrollTrigger.refresh during resize).
  const exitTlRef = useRef<gsap.core.Timeline | null>(null)

  const displayedSection = sections[displayedIndex]
  const displayedProject =
    displayedSection?.type === "project" && projects
      ? (projects.find((p) => p.id === displayedSection.projectId) ?? null)
      : null

  useEffect(() => {
    if (activeSectionIndex === displayedIndexRef.current) return
    const el = contentRef.current

    if (!el) {
      displayedIndexRef.current = activeSectionIndex
      setDisplayedIndex(activeSectionIndex)
      return
    }

    // Kill the exit timeline directly — NOT via killTweensOf(splitLines).
    // killTweensOf on child elements removes them from the timeline, leaving
    // an empty timeline that immediately fires onComplete with a stale closure.
    exitTlRef.current?.kill()
    exitTlRef.current = null
    gsap.killTweensOf(el)

    const swap = () => {
      displayedIndexRef.current = activeSectionIndex
      setDisplayedIndex(activeSectionIndex)
      exitTlRef.current = null
    }

    const splitLines = el.querySelectorAll<HTMLElement>(".split-line")

    if (splitLines.length > 0) {
      // ── Mask exit: lines slide down behind their overflow:hidden clips ──────
      const tl = gsap.timeline({ onComplete: swap })
      exitTlRef.current = tl
      tl.to(splitLines, {
        yPercent: 105,
        stagger: { each: 0.018, from: "start" },
        duration: 0.18,
        ease: "power2.in",
      })
    } else {
      // ── Fade for empty content (home/contact in panel) ────────────────────
      gsap.to(el, {
        opacity: 0,
        y: -10,
        duration: 0.16,
        ease: "power2.in",
        onComplete: () => {
          swap()
          gsap.fromTo(el, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.24, ease: "power2.out" })
        },
      })
    }
  }, [activeSectionIndex])

  return (
    <div data-fixed-info-panel className="pointer-events-none flex w-full flex-col justify-start pt-24 pb-8 md:w-3/5">
      <div ref={contentRef} data-panel-content>
        {displayedSection?.type === "project" && displayedProject && (
          <ProjectContent key={displayedProject.id} project={displayedProject} />
        )}
      </div>
    </div>
  )
}

// ─── ProjectContent ───────────────────────────────────────────────────────────

function ProjectContent({ project }: { project: Project }) {
  const containerRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!containerRef.current) return

      const titleEl = containerRef.current.querySelector<HTMLElement>("[data-text='title']")
      const descEl = containerRef.current.querySelector<HTMLElement>("[data-text='desc']")
      const typeEl = containerRef.current.querySelector<HTMLElement>("[data-text='type']")

      const splits: SplitText[] = []
      const lines: Element[] = []

      const split = (el: HTMLElement | null) => {
        if (!el) return
        const s = new SplitText(el, { type: "lines", linesClass: "split-line" })
        splits.push(s)
        lines.push(...s.lines)
      }

      split(titleEl)
      split(descEl)
      split(typeEl)

      gsap.set(lines, { yPercent: 105 })

      gsap
        .timeline()
        .to(lines, { yPercent: 0, stagger: 0.04, duration: 0.32, ease: "power2.out" })

      return () => splits.forEach((s) => s.revert())
    },
    { scope: containerRef, dependencies: [project.id] },
  )

  return (
    <div ref={containerRef} data-project-info className="flex flex-col gap-4">
      <div data-project-logo>
        <ClientLogo
          src={project.metadata.clientLogo}
          alt={project.metadata.client[0] ?? project.title ?? "Client logo"}
          className="h-10 w-auto max-w-[16rem] object-contain md:h-8 md:max-w-40"
        />
      </div>
      <div className="overflow-hidden">
        <h2 data-text="title" className="pb-2 text-3xl font-black tracking-tight text-foreground md:text-5xl">
          {project.title ?? "Untitled"}
        </h2>
      </div>

      {project.description && (
        <div className="overflow-hidden">
          <p data-text="desc" className="text-sm leading-relaxed text-foreground/65 md:text-xl">
            {project.description}
          </p>
        </div>
      )}

      {project.projectType && (
        <div className="overflow-hidden">
          <p
            data-text="type"
            className="inline-flex w-fit items-center border border-foreground/12 bg-background/72 px-3 py-2 text-[0.72rem] font-light uppercase tracking-[0.16em] text-foreground/72 [font-family:var(--font-sans)]"
          >
            {project.projectType}
          </p>
        </div>
      )}

    </div>
  )
}
